import { apiIsoStringFormat, parseDayJs, parseDayJsRange } from '@/Infrastructure/Utilities/dayjs.type';
import { getObjectChanges, removeUndefinedProperties } from '@/Infrastructure/Utilities/object.type';
import { Consignment, ConsignmentLeg, MutableConsignment, WorkOrderContact } from '@api/Trinium/Endpoints/Consignment/types';
import { Leg, LegDriver, LegLocation, LegNotes, MutatableLegRaw } from '@api/Trinium/Endpoints/Leg/types';
import { LegStatus, LegType, ParsedDate } from '@api/Trinium/types';
import { convertPoundsToKilograms } from '@forms/Container/types';
import { AssignedDriver, mapLegToDriverForm } from '@forms/Driver/types';
import { Dayjs } from 'dayjs';
import Yup from '@yup';
import { Customer } from '@api/Trinium/Endpoints/Customer/types';
import { MutableJob } from '@api/Trinium/Endpoints/Job/types';
import { City } from '@api/Trinium/Endpoints/Location/types';
import { Vessel, Voyage } from '@api/Trinium/Endpoints/Vessels/types';
import { JobCategory } from '@api/Trinium/Endpoints/JobCategories/types';
import { ContainerProvider, HazmatClass, HazmatUnNumber } from '@api/Trinium/Endpoints/Lookups/types';
import { getStringOrBlank } from '@/Infrastructure/Utilities/string.type';

export interface BaseConsignmentFields extends Record<string, unknown> {
  no: number;

  isWorkflow: boolean;
  isOverweight: boolean;
  isEdi: boolean;
  isStreetTurn: boolean;
  isLive: boolean;
  isBulk: 'Yes' | 'No' | null;
  isRailBilled: boolean;
  createdUserId: string;
  createdUser: string;
  createdDate: ParsedDate;

  /** Order Fields */
  jobCategory: JobCategory | null;
  masterBol?: string;
  bookingRefNo?: string;

  /** Customer Fields */
  customer?: Customer | null;
  customerRef?: string | null;
  woContactId?: number | null;
  workOrderContact?: WorkOrderContact | null;

  /** Container Fields */
  consignmentCount: number;
  cntrId?: string;
  container: ConsignmentContainer | null;
  vessel: Vessel | null;
  voyage: Voyage | null;

  /** Hazmat Fields */
  isHazmat: boolean;
  hazmatClasses?: HazmatClass[];
  hazmatUnNumbers?: HazmatUnNumber[];
  hazEmergencyContact?: string;

  firstReceivedDate?: Dayjs | null;

  pu?: LegLocation | null;
  puRefNo?: string;
  puScheduledDateRange?: string | null;
  lastFreeDay: Dayjs | null;
  cutoff: Dayjs | null;

  delv?: LegLocation | null;
  delvRefNo?: string;
  delvScheduledDateRange?: string | null;

  dehire?: LegLocation | null;
  dehireScheduledPickupDateRange?: string | null;
  dehireScheduledDeliveryDateRange?: string | null;
}

/** Fields that are copied to a new Order when using the Copy Order functionality */
export interface CopyConsignmentFields {
  consignmentId: number;
  includeLegs: boolean;
  includeCharges: boolean;
  /** The source order has legs beyond its defaults (e.g. a pre-pull), so when its legs are copied no pull or split legs can be added on top */
  hasAddedLegs: boolean;
  isWorkflow: boolean;
  // Explicitly not supposed to be copied #11827
  // isOverweight: boolean;
  isEdi: boolean;
  isStreetTurn: boolean;
  isLive: boolean;
  isBulk: 'Yes' | 'No' | null;
  isRailBilled: boolean;

  /** Order Fields */
  jobCategory: JobCategory | null;
  masterBol?: string;
  bookingRefNo?: string;

  /** Customer Fields */
  customer?: Customer | null;
  woContactId?: number | null;
  workOrderContact?: WorkOrderContact | null;

  /** Container Fields */
  container: Omit<ConsignmentContainer, 'contents' | 'relNo' | 'sealNo' | 'weightInPounds' | 'weightInKg' | 'temp' | 'pieces'> | null;

  pu?: LegLocation | null;
  delv?: LegLocation | null;
}

export interface CopyConsignmentPromptForm {
  includeLegs: boolean;
  includeCharges: boolean;
}

export interface CancelReasonPromptForm {
  cancelReason: string;
}

/** Mirrors the history note rules cancelord.p enforces through d-fhd-40.w. */
export const CancelReasonValidationSchema = Yup.object<CancelReasonPromptForm>().shape({
  cancelReason: Yup.string()
    .trim()
    .required('History notes are required when cancelling an order.')
    .min(2, 'History notes should contain more than one character when cancelling an order.')
});

export function toCopyConsignment(consignment: Consignment): CopyConsignmentFields {
  return {
    consignmentId: consignment.id,
    includeLegs: false,
    includeCharges: false,
    // Matches the API: a local order defaults to 1 leg, every other order to 2 (FCL and ECL)
    hasAddedLegs: consignment.consignmentLegs.length > (consignment.job.catTransType.toUpperCase() === 'L' ? 1 : 2),

    isWorkflow: consignment.isWorkflow,
    // Explicitly not supposed to be copied #11827
    // isOverweight: consignment.isOverweight,
    isEdi: consignment.isEdi ?? false,
    isStreetTurn: consignment.isStreetTurn ?? false,
    isLive: consignment.isLive,
    isBulk: consignment.isBulk ? 'Yes' : 'No',
    isRailBilled: consignment.isRailBilled,
    jobCategory: {
      id: consignment.job.catId,
      name: consignment.job.catName,
      transType: consignment.job.catTransType
    },
    masterBol: consignment.job.masterBol,
    bookingRefNo: consignment.job.bookingRefNo,
    customer: {
      id: consignment.custId,
      name: consignment.cust.name,
      addr1: consignment.cust.addr1,
      addr2: consignment.cust.addr2,
      city: consignment.cust.city,
      zip: consignment.cust.zip,
      creditLmt: consignment.cust.creditLmt,
      availCredit: consignment.cust.availCredit,
      creditSts: consignment.cust.creditSts,
      hasCreditWrn: consignment.cust.hasCreditWrn
    },
    woContactId: consignment.woContactId,
    workOrderContact: consignment.workOrderContact,
    container: {
      ...(consignment.cntr?.providerId && consignment.cntr.providerName
        ? {
            cntrProvider: {
              id: consignment.cntr.providerId,
              name: consignment.cntr.providerName
            }
          }
        : {}),
      size: consignment.cntr?.size,
      type: consignment.cntr?.type,
      reefer: consignment.cntr?.reefer
    },
    pu: consignment.pu,
    delv: consignment.delv
  };
}

/**
 * Formik schema for the Consignment Details. Only a single leg can be rendered at any given time for the Consignment at this time
 * and must be saved before leaving the form.
 */
export type ConsignmentFormikValues = BaseConsignmentFields &
  Record<
    string,
    | City
    | ConsignmentContainer
    | ConsignmentLeg
    | Customer
    | Dayjs
    | HazmatClass[]
    | HazmatUnNumber[]
    | LegFormikValues
    | LegLocation
    | JobCategory
    | ParsedDate
    | Vessel
    | Voyage
    | WorkOrderContact
    | string
    | number
    | boolean
    | undefined
    | null
  > & {
    id: number;
    jobId: number;
    legCount: number;
  };

export interface ConsignmentContainer {
  cntrProvider?: ContainerProvider | null;
  size?: string;
  type?: string;
  reefer?: boolean;
  temp?: number | null;
  pieces: number | null;
  weightInPounds: number | null;
  weightInKg: number | null;
  contents?: string;
  sealNo?: string;
  relNo?: string;
}

export const legValidationSchema = Yup.object<LegFormikValues>().shape({
  /** Leg Header Validation */
  releaseNo: Yup.string().nullable().typeError('Invalid string format').max(40, 'Release # exceeds max length (40)'),
  ediRefNo: Yup.string().nullable().max(30, 'EDI Ref # exceeds max length (30)'),
  /** Location Details Validation */
  scheduledPickupDateRange: Yup.string().nullable().isValidDateRangeString(),
  actualPickupDateTime: Yup.date()
    .nullable()
    .typeError('Invalid Date Time')
    .when('$isModified', {
      is: true,
      then: (me) => me.annualMinRange().annualMaxRange()
    })
    .when('hasActualPickupTime', {
      is: (val: boolean) => val,
      then: (me) => me.required('Actual appointment times cannot be removed')
    })
    .when('actualDeliveryDateTime', {
      is: (val: Dayjs | null) => val !== null,
      then: (me) => me.required('Actual Delivery Date cannot be entered without an Actual Pickup Date')
    }),
  scheduledDeliveryDateRange: Yup.string().nullable().isValidDateRangeString(),
  actualDeliveryDateTime: Yup.date()
    .nullable()
    .typeError('Invalid Date Time')
    .when('$isModified', {
      is: true,
      then: (me) => me.annualMinRange().annualMaxRange()
    })
    .when('hasActualDeliveryTime', {
      is: (val: boolean) => val,
      then: (me) => me.required('Actual Delivery time cannot be removed')
    }),
  /** Container Details Validation */
  emptyAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),
  fullAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),
  chassisAvailableDate: Yup.date().nullable().typeError('Invalid Date Time').containerMaxRange(),

  sealNo: Yup.string().nullable().typeError('Invalid String Format').max(55, 'Seal # exceeds max length (55)'),

  containerNumber: Yup.string().nullable().max(13, 'Container # exceeds max length (13)'),
  weightInPounds: Yup.number().nullable().max(99999, 'Weight (lbs) cannot exceed 99,999')
  // .matches(/^[a-zA-Z]{4}/, 'First four characters must be letters')
});

/**
 * The Leg Details Form for the currently selected leg in the Consignment Details modal
 */
export type LegFormikValues = Record<
  string,
  | LegLocation
  | LegStatus
  | LegType
  | LegNotes[]
  | LegDriver
  | AssignedDriver
  | Dayjs
  | [Dayjs | null, Dayjs | null]
  | boolean
  | number
  | string
  | null
  | undefined
> & {
  // ---- Identifier/Flags fields needed throughout renders -------------
  /** Leg Identifier */
  id?: number;

  // ---- Leg Details & Notes -------------------------------------
  no?: number;
  status?: keyof typeof LegStatus;
  isActiveLeg?: boolean;
  isLive?: boolean;
  isInvoiced?: boolean;
  isReminder?: boolean;
  type?: keyof typeof LegType;
  miles?: number | null;
  releaseNo?: string;
  ediRefNo?: string;
  /** Flag passed down to the Driver input for validation requirements */
  requiresHazmatCertification?: boolean;
  /** Represents the driver entity. Has additional properties that the assignedDriver does not contain. */
  driver?: LegDriver;
  /** Represents the selected value of the Driver Data Grid for the Driver Button Dropdown */
  assignedDriver: AssignedDriver | null;
  driverNotes?: string;
  dispatchNotes?: LegNotes[];

  // ---- Container Details -------------------------------
  fullAvailableDate: Dayjs | null;
  emptyAvailableDate: Dayjs | null;
  chassisAvailableDate: Dayjs | null;
  lastFreeDate: Dayjs | null;
  cutoffDate: Dayjs | null;
  containerNumber?: string;
  size?: string;
  cntrtype?: string;
  streetTurn: Dayjs | null;
  pieces: number | null;
  weightInPounds: number | null;
  weightInKg: number | null;
  sealNo: string | null;
  chassisNumber?: string | null;
  catTransType?: string;
  vessel?: string;
  voyage?: string;
  firstReceiveDate?: Dayjs | null;

  // ---- Location Details -------------------------------
  scheduledPickupDateRange: string | null;
  actualPickupDateTime: Dayjs | null;
  /** Validation flag indicating that Actual Pickup Time was received initially meaning it cannot be null'ed */
  hasActualPickupTime: boolean;
  pickupLocation: LegLocation | null;
  scheduledDeliveryDateRange: string | null;
  actualDeliveryDateTime: Dayjs | null;
  /** Validation flag indicating that Actual Delivery Time was received initially meaning it cannot be null'ed */
  hasActualDeliveryTime: boolean;
  deliveryLocation: LegLocation | null;
};

/**
 * Determines the value for a date field when mapping form changes to API format.
 * @param changedValue - The new value if it was changed, undefined if unchanged
 * @param initialValue - The original value from the form
 * @param shouldUseInitialAsFallback - Whether to use the initial value when the field wasn't directly changed
 * @returns Formatted date string, null to clear the value, or undefined to leave unchanged
 */
export function getDateFieldValue(
  changedValue: Dayjs | null | undefined,
  initialValue: Dayjs | null | undefined,
  shouldUseInitialAsFallback: boolean
): string | null | undefined {
  if (changedValue !== undefined) {
    // If the value was changed, return the new value formatted for the API (or null if cleared)
    return changedValue?.format(apiIsoStringFormat) ?? null;
  }
  if (shouldUseInitialAsFallback) {
    // If we need to use the fallbeck initial value (e.g. for Aux Avl. fields when they are changed but the date field itself is not), return the initial value formatted for the API (or null if it was originally null)
    return initialValue?.format(apiIsoStringFormat) || null;
  }

  return undefined;
}

/**
 * When the Consignment Formik Modal is rendered there is not guaranteed to be a leg selected
 * and the leg fields should render empty until a leg is fetched from the API.
 */
export function createConsignmentFormik(consignment: Consignment): ConsignmentFormikValues {
  // Presence of hazmat data flags the consignment as hazmat
  const isHazmat: boolean =
    (consignment.haz?.classes?.length ?? 0) > 0 || (consignment.haz?.unNos?.length ?? 0) > 0 || Boolean(consignment.haz?.contact);

  return {
    id: consignment.id,
    no: consignment.no,
    consignmentCount: consignment.job.csnCount ?? 1,
    isWorkflow: consignment.isWorkflow,
    isOverweight: consignment.isOverweight,
    isEdi: consignment.isEdi ?? false,
    isStreetTurn: consignment.isStreetTurn ?? false,
    isLive: consignment.isLive,
    isBulk: consignment.isBulk ? 'Yes' : 'No',
    isRailBilled: consignment.isRailBilled,
    createdDate: consignment.dateCreated,
    createdUser: consignment.createdUsrName,
    createdUserId: consignment.createdUsrId,
    lastFreeDay: parseDayJs(consignment.lastFreeDay?.unix),
    cutoff: parseDayJs(consignment.cuttoff?.unix),
    firstReceivedDate: parseDayJs(consignment.firstReceivedDate?.unix),

    jobId: consignment.jobId,
    masterBol: consignment.job.masterBol ?? '',
    bookingRefNo: consignment.job.bookingRefNo ?? '',
    jobCategory: {
      id: consignment.job.catId,
      name: consignment.job.catName,
      transType: consignment.job.catTransType
    },

    customerRef: consignment.custRefNo ?? '',
    customer: {
      id: consignment.custId,
      name: consignment.cust.name,
      addr1: consignment.cust.addr1,
      addr2: consignment.cust.addr2,
      city: consignment.cust.city,
      state: undefined, // TODO: When Chris returns ask him if this is not provided for a reason,
      zip: consignment.cust.zip,
      creditLmt: consignment.cust.creditLmt,
      availCredit: consignment.cust.availCredit,
      creditSts: consignment.cust.creditSts,
      salesperson: consignment.slsId
        ? {
            id: consignment.slsId,
            name: consignment.sls?.name ?? '-'
          }
        : undefined,
      hasCreditWrn: consignment.cust.hasCreditWrn
    },

    woContactId: consignment.woContactId,
    workOrderContact: {
      id: consignment.workOrderContact?.id ?? 0,
      name: getStringOrBlank(consignment.workOrderContact?.name),
      phone: getStringOrBlank(consignment.workOrderContact?.phone),
      email: getStringOrBlank(consignment.workOrderContact?.email),
      city: consignment.workOrderContact?.city ?? null,
      role: getStringOrBlank(consignment.workOrderContact?.role)
    },

    isHazmat: isHazmat,
    hazmatClasses: consignment.haz?.classes ?? [],
    hazmatUnNumbers: consignment.haz?.unNos ?? [],
    hazEmergencyContact: getStringOrBlank(consignment.haz?.contact),

    cntrId: consignment.cntrId ?? '',
    container: {
      cntrProvider: consignment.cntr?.providerId
        ? ({
            id: consignment.cntr.providerId,
            name: consignment.cntr.providerName
          } as ContainerProvider)
        : null,
      size: consignment.cntr?.size,
      type: consignment.cntr?.type,
      reefer: false,
      pieces: consignment.cntr?.pcs ?? null,
      weightInPounds: consignment.cntr?.wgtLb ?? null,
      weightInKg: consignment.cntr?.wgtLb ? convertPoundsToKilograms(consignment.cntr.wgtLb) : null,
      contents: consignment.cntr?.contents ?? '',
      sealNo: consignment.cntr?.sealNo ?? '',
      relNo: consignment.cntr?.emptyRelNo ?? '',
      temp: consignment.cntr?.temp ?? null
    },

    vessel: consignment.job.vessel ?? null,
    voyage: consignment.job.voyage ?? null,

    releaseNo: '',

    dehire: consignment.dehire ?? null,

    pu: consignment.pu ?? null,
    puRefNo: consignment.puRefNo ?? '',
    puScheduledDateRange: consignment.scheduledPickupRange,

    delv: consignment.delv ?? null,
    delvRefNo: consignment.delvRefNo ?? '',
    delvScheduledDateRange: consignment.scheduledDeliveryRange,

    legCount: consignment.consignmentLegs.length
  };
}

/**
 * Mapping function to relate an API Leg Entity to a Formik Leg schema
 */
export function mapLegToFormikForm(leg: Leg): LegFormikValues {
  return {
    id: leg.id,
    no: leg.no,
    status: leg.status,
    isActiveLeg: leg.isActiveLeg,
    isLive: leg.csn.isLive,
    isInvoiced: leg.isInvoiced,
    isReminder: leg.isReminder,
    type: leg.type,
    miles: leg.miles ? Math.round(leg.miles) : null,
    ediRefNo: leg.ediRefNo ?? '',
    releaseNo: leg.relNo ?? '',
    driverNotes: leg.drvNotes ?? '',
    dispatchNotes: leg.notes,
    driver: leg.drv,
    assignedDriver: mapLegToDriverForm(leg),
    requiresHazmatCertification: leg.reqHazmatCert,

    containerNumber: leg.cntrId ?? '',
    size: leg.cntrSize,
    cntrtype: leg.cntrType,
    streetTurn: parseDayJs(leg.streetTurnDate?.unix),
    chassisNumber: leg.chassisId ?? '',
    fullAvailableDate: parseDayJs(leg.fullAvailableDate?.unix),
    emptyAvailableDate: parseDayJs(leg.emptyAvailableDate?.unix),
    chassisAvailableDate: parseDayJs(leg.chassisAvailableDate?.unix),
    pieces: leg.pcs ?? null,
    weightInPounds: leg.wgtLb ?? null,
    weightInKg: leg.wgtLb ? convertPoundsToKilograms(leg.wgtLb) : null,
    sealNo: leg.puSealNo ?? '',
    dehireId: leg.dehireId ?? '',
    lastFreeDate: parseDayJs(leg.lastFreeDate?.unix),
    cutoffDate: parseDayJs(leg.cutoffDate?.unix),
    catTransType: leg.job?.catTransType,
    firstReceiveDate: parseDayJs(leg.firstReceiveDate?.unix),

    scheduledPickupDateRange: leg.scheduledPickupDateFrom
      ? `${leg.scheduledPickupDateFrom.dateTime}${leg.scheduledPickupDateTo?.time ? ` - ${leg.scheduledPickupDateTo.time}` : ''}`
      : null,
    actualPickupDateTime: parseDayJs(leg.pickupDate?.unix),
    hasActualPickupTime: Boolean(leg.pickupDate),
    pickupLocation: leg.pickupLocation ?? null,
    scheduledDeliveryDateRange: leg.scheduledDeliveryDateFrom
      ? `${leg.scheduledDeliveryDateFrom.dateTime}${leg.scheduledDeliveryDateTo?.time ? ` - ${leg.scheduledDeliveryDateTo.time}` : ''}`
      : null,
    actualDeliveryDateTime: parseDayJs(leg.delvDate?.unix),
    hasActualDeliveryTime: Boolean(leg.delvDate),
    deliveryLocation: leg.deliveryLocation ?? null
  };
}

export interface ApiConsignmentFormik {
  /** ID of the job being mutated */
  jobId: number;
  /** Mutations to the Job record made on the form */
  jobEndpointChanges: MutableJob;
  /** ID of the consignment being mutated */
  consignmentId: number;
  /** Mutations to the Consignment record made on the form */
  consignmentEndpointChanges: Partial<MutableConsignment>;
  // /** ID of the leg being mutated */
  // legId?: number;
  // /** Mutations to the Leg record made on the form */
  // legEndpointChanges: MutatableLegRaw;
}

/**
 * Function to split the ConsignmentFormikValues object into the individual API schemas for all the different APIs
 * that can possibly be called based on what values were changed.
 * @param formValues
 * @param initialFormValues
 * @returns
 */
export function csnFormikToApiObjects(
  formValues: ConsignmentFormikValues,
  initialFormValues: ConsignmentFormikValues
): ApiConsignmentFormik {
  /** Determine what changes have been made to the form */
  const valuesToPost = getObjectChanges(formValues, initialFormValues);

  const jobUpdates: Partial<MutableJob> = {
    masterBol: valuesToPost.masterBol,
    bookingRefNo: valuesToPost.bookingRefNo,
    /** This would be pre-validated on the form */
    allowDupMasterBol: valuesToPost.masterBol ? true : undefined,
    allowDupBookingRefNo: valuesToPost.bookingRefNo ? true : undefined,
    category: valuesToPost.jobCategory ?? undefined,
    ...(valuesToPost.vessel !== undefined ? { vessel: valuesToPost.vessel === null ? null : formValues.vessel } : {}),
    ...(valuesToPost.voyage !== undefined ? { voyage: valuesToPost.voyage === null ? null : formValues.voyage } : {})
  };

  const scheduledPickupDates = parseDayJsRange(valuesToPost.puScheduledDateRange);
  const scheduledDeliveryDates = parseDayJsRange(valuesToPost.delvScheduledDateRange);

  const consignmentUpdates: Partial<MutableConsignment> = {
    puRefNo: valuesToPost.puRefNo,
    delvRefNo: valuesToPost.delvRefNo,
    isLive: valuesToPost.isLive,
    isStreetTurn: valuesToPost.isStreetTurn,
    isOverweight: valuesToPost.isOverweight,
    custId: valuesToPost.customer?.id,
    custRefNo: valuesToPost.customerRef,
    /** This would be pre-validated on the form */
    allowDupCustRefNo: valuesToPost.customerRef ? true : undefined,

    lastFreeDate: valuesToPost.lastFreeDay
      ? convertFormDateToApiRequest(valuesToPost.lastFreeDay, initialFormValues.lastFreeDay)
      : undefined,

    cutoffDate: valuesToPost.cutoff !== undefined ? convertFormDateToApiRequest(valuesToPost.cutoff, initialFormValues.cutoff) : undefined,

    frstRcvDate:
      valuesToPost.firstReceivedDate !== undefined
        ? convertFormDateToApiRequest(valuesToPost.firstReceivedDate, initialFormValues.firstReceivedDate)
        : undefined,

    /** If delivery dates were in the object changes, than it either has a value or they blanked it out */
    schdDelvFrom:
      valuesToPost.delvScheduledDateRange !== undefined && scheduledDeliveryDates.length > 0
        ? convertFormDateToApiRequest(scheduledDeliveryDates[0])
        : undefined,
    schdDelvTo:
      valuesToPost.delvScheduledDateRange !== undefined && scheduledDeliveryDates.length > 1
        ? convertFormDateToApiRequest(scheduledDeliveryDates[1])
        : undefined,
    schdPuFrom:
      valuesToPost.puScheduledDateRange !== undefined && scheduledPickupDates.length > 0
        ? convertFormDateToApiRequest(scheduledPickupDates[0])
        : undefined,
    schdPuTo:
      valuesToPost.puScheduledDateRange !== undefined && scheduledPickupDates.length > 1
        ? convertFormDateToApiRequest(scheduledPickupDates[1])
        : undefined,

    ...(valuesToPost.dehire === null ? { dehireId: null } : { dehireId: valuesToPost.dehire?.id }),

    ...(valuesToPost.delv === null ? { delvId: null } : { delvId: valuesToPost.delv?.id }),

    ...(valuesToPost.pu === null ? { puId: null } : { puId: valuesToPost.pu?.id }),

    woContactId: valuesToPost.woContactId,

    // Only include the Work Order Contact fields if they changed when the WO Contact itself wasn't changed.
    // This is resolving an issue where the initial form values does not reflect that the name/phone/email were changed
    // with the contact but are actually 'new' initial values.
    ...(valuesToPost.workOrderContact && initialFormValues.woContactId === formValues.woContactId
      ? {
          woContactId: formValues.woContactId,
          woContact: {
            name: valuesToPost.workOrderContact.name,
            phone: valuesToPost.workOrderContact.phone,
            email: valuesToPost.workOrderContact.email,
            cityId: valuesToPost.workOrderContact.city?.id
          }
        }
      : {}),

    cntrId: valuesToPost.cntrId,
    ...(valuesToPost.container
      ? {
          cntr: {
            temp: valuesToPost.container.temp,
            pcs: valuesToPost.container.pieces,
            wgtLb: valuesToPost.container.weightInPounds,
            contents: valuesToPost.container.contents,
            sealNo: valuesToPost.container.sealNo,
            emptyRelNo: valuesToPost.container.relNo,
            providerId: valuesToPost.container.cntrProvider?.id,
            size: valuesToPost.container.size,
            type: valuesToPost.container.type
          } as Partial<ConsignmentContainer>
        }
      : {}),

    // Prioritize isHazmat flag because properties don't matter if that's false. Otherwise POST will try to null out each property
    ...(valuesToPost.isHazmat === false
      ? { haz: null }
      : Object.keys(valuesToPost).includes('hazEmergencyContact') || valuesToPost.hazmatClasses || valuesToPost.hazmatUnNumbers
        ? {
            haz: {
              classes: valuesToPost.hazmatClasses,
              unNos: valuesToPost.hazmatUnNumbers,
              contact: valuesToPost.hazEmergencyContact
            }
          }
        : {}),
    isBulk: valuesToPost.isBulk ? valuesToPost.isBulk === 'Yes' : undefined
  };

  return {
    jobId: formValues.jobId,
    jobEndpointChanges: removeUndefinedProperties(jobUpdates),
    consignmentId: formValues.id,
    consignmentEndpointChanges: removeUndefinedProperties(consignmentUpdates)
  };
}

/**
 * Mapping function to prepare the Leg Sub-Form Formik to be submitted to the API
 * @param formValues Current Leg-Sub-Form values
 * @param initialFormValues Initial Leg-Sub-Form values
 * @returns MutableLegRaw to send to the Leg API
 */
export function csnFormikToMutableLeg(legFormValues?: Partial<LegFormikValues>, initialFormValues?: LegFormikValues): MutatableLegRaw {
  if (!legFormValues) {
    return {};
  }

  const formValues = getObjectChanges(legFormValues, initialFormValues);
  const legType = legFormValues.type ? LegType[legFormValues.type] : undefined;
  /** The API requires the Available Date to be provided for the PUT when updating Aux Avl. Fields */
  const includeAvailableDate = formValues.lastFreeDate !== undefined || formValues.cutoffDate !== undefined;

  const scheduledPickupDates = parseDayJsRange(formValues.scheduledPickupDateRange);
  const scheduledDeliveryDates = parseDayJsRange(formValues.scheduledDeliveryDateRange);
  /**
   * Map form changes to a MutableLegRaw for API consumption
   */
  const updates: MutatableLegRaw = {
    /** Leg Details */
    miles: formValues.miles,
    type: formValues.type,
    drvNotes: formValues.driverNotes,
    ediRefNo: formValues.ediRefNo,
    relNo: formValues.releaseNo,
    drvId: formValues.assignedDriver ? formValues.assignedDriver.driver?.id : formValues.assignedDriver,
    tractorId: formValues.assignedDriver?.tractorId,
    isReminder: formValues.isReminder,

    /** Location Sub-Form */

    schdPuFrom:
      formValues.scheduledPickupDateRange !== undefined ? (scheduledPickupDates[0]?.format(apiIsoStringFormat) ?? null) : undefined,
    schdPuTo: formValues.scheduledPickupDateRange !== undefined ? (scheduledPickupDates[1]?.format(apiIsoStringFormat) ?? null) : undefined,
    puDate: formValues.actualPickupDateTime?.format(apiIsoStringFormat) ?? undefined,
    puId: formValues.pickupLocation !== undefined ? (legFormValues.pickupLocation?.id ?? null) : undefined,

    schdDelvFrom:
      formValues.scheduledDeliveryDateRange !== undefined ? (scheduledDeliveryDates[0]?.format(apiIsoStringFormat) ?? null) : undefined,
    schdDelvTo:
      formValues.scheduledDeliveryDateRange !== undefined ? (scheduledDeliveryDates[1]?.format(apiIsoStringFormat) ?? null) : undefined,
    // Actual Delivery
    delvDate: formValues.actualDeliveryDateTime?.format(apiIsoStringFormat) ?? undefined,
    delvId: formValues.deliveryLocation !== undefined ? (legFormValues.deliveryLocation?.id ?? null) : undefined,

    /** Container Sub-Form */
    cntrId: formValues.containerNumber,
    chassisId: formValues.chassisNumber,
    pcs: formValues.pieces,
    wgtLb: formValues.weightInPounds,
    puSealNo: formValues.sealNo ?? undefined,
    streetTurnDate: formValues.streetTurn !== undefined ? (formValues.streetTurn?.format(apiIsoStringFormat) ?? null) : undefined,
    /** If full available is included in the changes, set it. Or if last free day changed on an FCL, set it. */
    fullAvailDate: getDateFieldValue(
      formValues.fullAvailableDate,
      initialFormValues?.fullAvailableDate,
      includeAvailableDate && legType === LegType.FCL
    ),
    /** If empty available is included in the changes, set it. Or if last free day changed on an ECL, set it. */
    emptyAvailDate: getDateFieldValue(
      formValues.emptyAvailableDate,
      initialFormValues?.emptyAvailableDate,
      includeAvailableDate && legType === LegType.ECL
    ),
    /** If last free day is included in the changes set it. Or if either availability changed, set it. */
    lastFreeDate: getDateFieldValue(formValues.lastFreeDate, initialFormValues?.lastFreeDate, formValues.fullAvailableDate !== undefined),
    cutoffDate: getDateFieldValue(formValues.cutoffDate, initialFormValues?.cutoffDate, formValues.emptyAvailableDate !== undefined),
    chassisAvailDate:
      formValues.chassisAvailableDate !== undefined ? (formValues.chassisAvailableDate?.format(apiIsoStringFormat) ?? null) : undefined
  };

  /**
   * If only the tractor changed, apply the original driver id to the response because they are used in unison
   * for a PUT request and we cannot only send the tractor ID
   */
  if (formValues.tractorId && !formValues.drvId) {
    formValues.drvId = initialFormValues?.assignedDriver?.driver?.id;
  }

  return removeUndefinedProperties(updates);
}

/**
 * The API returns errors using the field as it knows it, not the formik field name. This function
 * provides a mapping from the api response to a formik field that it correlates to, if able, resulting
 * in the field getting a red border with a hover error message.
 */
export const legApiFieldMappings: Record<keyof ConsignmentFormikValues, string | Record<string, string>> = {
  // ---- Leg Details & Notes -----------------------------
  type: 'type',
  miles: 'miles',
  driverNotes: 'drvNotes',
  ediRefNo: 'ediRefNo',
  releaseNo: 'relNo',
  assignedDriver: 'drvId',

  // ---- Container Details -------------------------------
  chassisNumber: 'chassisId',
  emptyAvailableDate: 'emptyAvailDate',
  containerNumber: 'cntrId',
  fullAvailableDate: 'fullAvailDate',
  streetTurn: 'streetTurnDate',
  pieces: 'pcs',
  sealNo: 'puSealNo',
  dehireId: 'dehireId',
  size: 'cntrSize',
  cntrType: 'type',
  weightInKg: 'wgtKg',
  weightInPounds: 'wgtLb',
  catTransType: 'catTransType',
  lastFreeDate: 'lastFreeDate',
  cutoffDate: 'cutoffDate',
  chassisAvailableDate: 'chassisAvailDate',

  // ---- Location Details --------------------------------
  scheduledPickupDateRange: 'schdPuFrom',
  actualPickupDateTime: 'puDate',
  hasActualPickupTime: '',
  pickupLocation: 'puId',

  scheduledDeliveryDateRange: 'schdDelvFrom',
  actualDeliveryDateTime: 'delvDate',
  hasActualDeliveryTime: '',
  deliveryLocation: 'delvId'
};

/** Maps API Errors to the Field Inputs on the Order Entry Tab */
export const orderEntryApiFieldMappings: Record<keyof ConsignmentFormikValues, string | Record<string, string>> = {
  jobCategory: 'jobCat',

  customer: { id: 'custId' },
  customerRef: 'customerRef',
  woContactId: 'woContactId',
  workOrderContact: {
    phone: 'woPhone',
    email: 'woEmail',
    city: 'woCityId'
  },

  pu: 'puId',
  puRefNo: 'puRefNo',
  puScheduledDateRange: '',

  delv: 'delvId',
  delvRefNo: 'delvRefNo',
  delvScheduledDateRange: '',

  dehire: 'dehireId',

  lastFreeDay: 'lastFreeDate',
  cutoff: 'cutoffDate',
  firstReceivedDate: 'frstRcvDate',

  cntrId: 'cntrId',
  container: {
    cntrProvId: 'cntrProvId',
    size: 'size',
    type: 'type',
    reefer: 'reefer',
    temp: 'temp',
    pieces: 'pieces',
    weightInPounds: 'weightInPounds',
    contents: 'contents',
    sealNo: 'sealNo',
    relNo: 'relNo'
  }
};

export enum ConsignmentTab {
  EDI,
  Order,
  Legs,
  Notes,
  Documents,
  ChargesAndPayments
}

import { FormikErrors, FormikValues } from 'formik';

/**
 * State exposed by Formik container components so parent components
 * can read dirty / error state without being inside the Formik context.
 */
export interface FormikFormState {
  readonly dirty: boolean;
  readonly isValid: boolean;
  readonly errors: FormikErrors<FormikValues>;
}

/**
 * Function to convert the dirty date input into the API value that should be passed in
 * @param currentValue The Current Form Value that is already determined dirty
 * @param initialValue The Initial Form Value to determine null vs undefined values
 * @returns
 */
export function convertFormDateToApiRequest(currentValue?: Dayjs | null, initialValue?: Dayjs | null): string | null | undefined {
  /** If the current value has a date, format it */
  if (currentValue?.isValid) {
    return currentValue.format(apiIsoStringFormat);
  }
  /** If both current value and initial value are null, we never had a value so don't include. Otherwise, set null to intend user blanked out the existing value */
  return currentValue == null && initialValue == null ? undefined : null;
}
